import type { JSX } from 'react'
import { useMemo, useState } from 'react'
import { Send } from 'lucide-react'
import type { BotEventDto } from '@/lib/dto'
import { api } from '@/lib/api'
import { problemText } from '@/screens/shared/problem'
import { Button } from '@/ui/primitives/Button'
import { Panel } from '@/ui/primitives/Surface'

/**
 * Чат бота: поле ввода и ЛЕНТА ОТВЕТОВ прямо под ним.
 *
 * Лента здесь не для красоты. На живом сервере отправка выглядела как «ничего не
 * произошло»: панель принимала сообщение (202 и очистка поля), бот его слал, а
 * сервер отвечал отказом — и этот ответ уезжал в общий лог, где его никто не
 * связывал с нажатием «Отправить». Ровно так и было с плагином чата, который
 * отвечает «Applicable chat not found» игроку без права писать в канал.
 *
 * Поэтому: последние строки чата видны там же, где их набирают, а ошибка самой
 * отправки (сеть, права, бот не в игре) показывается рядом с полем, а не молча
 * проглатывается.
 */

export interface ChatPanelProps {
  botId: string
  /** Общая лента событий бота — чат отбираем из неё, второго запроса не нужно. */
  events: readonly BotEventDto[]
  canOperate: boolean
  playing: boolean
}

/** Сколько строк чата держим перед полем ввода: экран, а не история. */
const VISIBLE = 8

export function ChatPanel({ botId, events, canOperate, playing }: ChatPanelProps): JSX.Element {
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [sending, setSending] = useState(false)

  // События приходят от новых к старым; для чтения нужен обратный порядок.
  const lines = useMemo(
    () =>
      events
        .filter(event => event.kind === 'chat')
        .slice(0, VISIBLE)
        .reverse(),
    [events]
  )

  const disabled = !canOperate || !playing

  async function submit(): Promise<void> {
    const message = text.trim()
    if (message === '' || disabled || sending) return
    setSending(true)
    setError(null)
    try {
      await api.bots.chat(botId, message)
      setText('')
    } catch (problem) {
      setError(problemText(problem))
    } finally {
      setSending(false)
    }
  }

  return (
    <Panel title="Чат" note="уходит на сервер как сообщение бота">
      {lines.length > 0 && (
        <div
          role="log"
          aria-label="Последние сообщения чата"
          className="mb-3 max-h-40 space-y-1 overflow-y-auto rounded-sm border border-line bg-panel-2 p-2 font-mono text-2xs text-ink-muted"
        >
          {lines.map(line => (
            <p key={line.id} className="break-words">
              {line.message}
            </p>
          ))}
        </div>
      )}

      <form
        className="flex items-center gap-2"
        onSubmit={event => {
          event.preventDefault()
          void submit()
        }}
      >
        <input
          value={text}
          onChange={event => setText(event.target.value)}
          maxLength={256}
          disabled={disabled}
          placeholder={playing ? 'Сообщение или /команда' : 'Бот не в игре'}
          aria-label="Сообщение в чат"
          aria-describedby={error === null ? undefined : 'chat-error'}
          className="min-w-0 flex-1 rounded-sm border border-line bg-panel-2 px-3 py-2 font-mono text-base text-ink placeholder:text-ink-faint focus:border-honey/60 disabled:opacity-45"
        />
        <Button type="submit" variant="primary" disabled={disabled || text.trim() === '' || sending}>
          <Send aria-hidden className="size-4" />
          Отправить
        </Button>
      </form>

      {error !== null && (
        <p id="chat-error" role="alert" className="mt-2 text-2xs text-fail">
          {error}
        </p>
      )}

      {/*
        Подсказка про отказ сервера: сообщение ушло, но плагин чата может его не
        принять. Без этой строки человек считает, что сломана панель.
      */}
      <p className="mt-2 text-2xs text-ink-faint">
        Ответ сервера появляется в ленте выше. Если он отвечает отказом («chat not
        found», «нет прав»), дело в правах аккаунта бота на самом сервере, а не в панели.
      </p>
    </Panel>
  )
}
